import React from 'react';
import { motion } from 'framer-motion';
import { Play, MapPin } from 'lucide-react';
import { Button } from './ui/Button';
import type { Season2Episode } from '../data/season2Episodes';
interface Season2EpisodeStoryProps {
  episode: Season2Episode;
  tone: 'dark-700' | 'dark-800';
}
export function Season2EpisodeStory({
  episode,
  tone
}: Season2EpisodeStoryProps) {
  const bg = tone === 'dark-700' ? 'bg-dark-700' : 'bg-dark-800';
  const ring = tone === 'dark-700' ? 'ring-dark-700' : 'ring-dark-800';
  return <section aria-labelledby={`s2-ep-${episode.id}`} className={`py-20 ${bg} border-t border-dark-600`} data-id="element-180" data-mp="1id8tykugxxff">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-181" data-mp="14dzc7gqm6v9c">
        {/* Episode header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12" data-id="element-182" data-mp="18eja4mj0i2wj">
          <div className="flex items-center gap-5" data-id="element-183" data-mp="3p3ylpunb5ce2">
            <img src={episode.image} alt="" className="w-20 h-20 md:w-24 md:h-24 rounded-xl object-cover border border-dark-600 shrink-0" data-id="element-184" data-mp="3t63bgiamxovf" />
            <div data-id="element-185" data-mp="1lyuptbyzbwz6">
              <p className="text-gold-500 text-sm font-semibold mb-1" data-id="element-186" data-mp="1lze48ipf838g">
                S2 · EP {episode.id}
                {episode.duration ? ` · ${episode.duration}` : ''}
              </p>
              <h2 id={`s2-ep-${episode.id}`} className="text-3xl md:text-4xl font-serif font-bold text-white" data-id="element-187" data-mp="1vwivgwy4iow4">
                {episode.title}
              </h2>
              <p className="flex items-center text-white/60 mt-1" data-id="element-188" data-mp="24yidnskvjz0n">
                with {episode.guest}
                <span className="mx-2 w-1 h-1 rounded-full bg-dark-400" data-id="element-189" data-mp="2jmwejxyqddek"></span>
                <MapPin className="w-3.5 h-3.5 mr-1" data-id="element-190" data-mp="2ens83f36xtrx" />
                {episode.hometown}
              </p>
            </div>
          </div>
          <a href={episode.spotifyUrl} target="_blank" rel="noopener noreferrer" className="shrink-0" data-id="element-191" data-mp="2so2ykh0l96ck">
            <Button leftIcon={<Play className="w-4 h-4 fill-current" data-id="element-193" data-mp="1lblhobkhext7" />} data-id="element-192" data-mp="1lbbzha5ohuy2">
              Listen on Spotify
            </Button>
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-194" data-mp="1qa50w47oxvhv">
          <div className="lg:col-span-7" data-id="element-195" data-mp="1uafiyf8rccd3">
            <h3 className="text-2xl font-serif font-bold text-white mb-6" data-id="element-196" data-mp="1u9dw7didq1fj">
              {episode.guest}'s Story
            </h3>
            <div className="space-y-6 text-lg text-white/80 leading-relaxed" data-id="element-197" data-mp="241jut634zh3p">
              {episode.description.map(paragraph => <p key={paragraph.slice(0, 24)} data-id="element-198" data-mp="28z38o4vx9rv4">{paragraph}</p>)}
            </div>
          </div>

          {/* Journey timeline */}
          <aside className="lg:col-span-5" aria-label={`${episode.guest}'s journey`} data-id="element-199" data-mp="2i12rn2qhz60p">
            <ol className="relative border-l border-dark-500 ml-3 space-y-10 lg:mt-14" data-id="element-200" data-mp="jlye3yomyt0z">
              {episode.journey.map((step, index) => <motion.li key={step.label} initial={{
              opacity: 0,
              x: -8
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.25,
              delay: index * 0.05,
              ease: [0.23, 1, 0.32, 1]
            }} className="pl-8" data-id="element-201" data-mp="nnbo8kyrhidp" data-mpi={index}>
                  <span className={`absolute -left-[7px] mt-2 h-3.5 w-3.5 rounded-full bg-gold-500 ring-4 ${ring}`} data-id="element-202" data-mp="z24x8noasidh"></span>
                  <h4 className="text-2xl font-serif font-bold text-white mb-1" data-id="element-203" data-mp="u43bfuzhm4yb">
                    {step.label}
                  </h4>
                  <p className="text-white/70" data-id="element-204" data-mp="l2vqsfpw92mp">{step.detail}</p>
                </motion.li>)}
            </ol>
          </aside>
        </div>
      </div>
    </section>;
}